import { useEffect, useState } from "react";
import { BellRing, X } from "lucide-react";
import { toast } from "sonner";
import { isPushSupported, getPushSubscription, subscribeToPush } from "@/lib/push";
import { useAuth } from "@/contexts/AuthContext";

const DISMISS_KEY = "kh_news_push_dismissed_v1";

/**
 * Slim, sticky banner that prompts logged-in users to enable Web Push so they
 * receive an OS-level notification whenever a news article is published.
 * Hides itself if push is already subscribed, unsupported, or user dismissed.
 */
export default function EnablePushBanner() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) { setShow(false); return; }
    if (!isPushSupported()) { setShow(false); return; }
    if (localStorage.getItem(DISMISS_KEY) === "1") { setShow(false); return; }
    (async () => {
      try {
        const sub = await getPushSubscription();
        setShow(!sub);
      } catch {
        setShow(false);
      }
    })();
  }, [user]);

  if (!show) return null;

  const enable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await subscribeToPush();
      toast.success("✅ تم تفعيل الإشعارات — سيصلك كل خبر جديد فوراً");
      setShow(false);
    } catch (e) {
      toast.error(e?.message || "تعذّر تفعيل الإشعارات");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
  };

  return (
    <div
      className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-4"
      data-testid="enable-push-banner"
    >
      <div className="flex items-center gap-3 rounded-2xl bg-gradient-to-l from-amber-100 via-amber-50 to-transparent dark:from-amber-900/40 dark:via-amber-800/20 dark:to-transparent border border-amber-300 dark:border-amber-700 p-3 shadow-sm">
        <div className="size-10 shrink-0 rounded-xl bg-amber-500 text-white flex items-center justify-center">
          <BellRing className="size-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-black text-sm text-amber-900 dark:text-amber-200 leading-tight">
            فعّل الإشعارات — يوصلك كل خبر جديد فوراً 📰
          </div>
          <div className="text-[11px] text-amber-800/80 dark:text-amber-300/80 mt-0.5 line-clamp-1">
            حتى لو التطبيق مقفل، سيظهر الإشعار على شاشتك.
          </div>
        </div>
        <button
          type="button"
          onClick={enable}
          disabled={busy}
          className="rounded-full bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold px-3 py-1.5 shrink-0 disabled:opacity-60"
          data-testid="enable-push-banner-btn"
        >
          {busy ? "..." : "تفعيل"}
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="size-7 rounded-full hover:bg-amber-200/50 dark:hover:bg-amber-800/50 flex items-center justify-center shrink-0"
          aria-label="إخفاء"
          data-testid="enable-push-banner-dismiss"
        >
          <X className="size-4 text-amber-800 dark:text-amber-300" />
        </button>
      </div>
    </div>
  );
}
